import { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroBg from "@/assets/hero-bg.webp";
import { trackEvent } from "@/lib/analytics";

const ROTATING_WORDS = ["Baselinker", "Shopify", "ShopGold", "Shoper", "Sellasist", "Subiekt GT", "Subiekt Nexo", "Fakturownia", "Apilo"];

const HERO_DEBUG =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).get("debugHero") === "1" ||
    window.localStorage.getItem("debugHero") === "1");

export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    if (HERO_DEBUG) console.log("[Hero] mount", { words: ROTATING_WORDS });
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => {
      setWordIndex((i) => {
        const next = (i + 1) % ROTATING_WORDS.length;
        if (HERO_DEBUG) console.log("[Hero] tick", { from: ROTATING_WORDS[i], to: ROTATING_WORDS[next] });
        return next;
      });
    }, 2800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (HERO_DEBUG) console.log("[Hero] render word", { wordIndex, word: ROTATING_WORDS[wordIndex] });
  }, [wordIndex]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-32 pb-20">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <img
          src={heroBg}
          alt=""
          width={1600}
          height={900}
          className="h-full w-full object-cover opacity-70 dark:opacity-90"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="animate-fade-up mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-1.5 text-xs">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>Wdrożenia • Integracje • Automatyzacja e-commerce</span>
        </div>

        <h1 className="animate-fade-up max-w-5xl font-display font-semibold leading-[1.05] tracking-tight text-[clamp(2.25rem,7vw,5.5rem)] [animation-delay:60ms]">
          <span className="block">
            <span>Wdrażam </span>
            <span
              className="relative inline-flex overflow-hidden align-bottom leading-[1.05]"
              style={{ height: "1.05em" }}
              aria-live="polite"
            >
              <span key={ROTATING_WORDS[wordIndex]} className="text-gradient word-swap inline-flex whitespace-nowrap">
                {ROTATING_WORDS[wordIndex]}
              </span>
            </span>
          </span>
          <span className="block">Napędzam Twoją sprzedaż</span>
        </h1>

        <p className="animate-fade-up mt-6 max-w-2xl text-lg text-muted-foreground [animation-delay:140ms]">
          Łączę sklepy, marketplace'y, ERP, marketing i obsługę klienta w jeden płynny ekosystem.
          Mniej manualnej pracy, więcej zamówień.
        </p>

        <div className="animate-fade-up mt-10 flex flex-wrap gap-4 [animation-delay:220ms]">
          <Button asChild size="lg" className="group h-12 px-7 text-base shadow-glow">
            <a
              href="#kontakt"
              onClick={() => trackEvent("cta_book_call", { location: "hero", method: "form" })}
            >
              Umów konsultację
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base">
            <a href="#uslugi">Zobacz, co robię</a>
          </Button>
        </div>

      </div>
    </section>
  );
}
